import React, { useEffect, useState, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Play, Pause, Square, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const isSubscriber = (user) => {
  if (!user || user.subscription_status === "free") return false;
  if (!user.subscription_expires) return false;
  return new Date(user.subscription_expires) > new Date();
};

// Very small helper to split content into speakable ~180 char chunks
const chunkText = (text) => {
  const sentences = text.replace(/\s+/g, " ").split(/(?<=[.!?])\s+/);
  const chunks = [];
  let buf = "";
  for (const s of sentences) {
    if ((buf + " " + s).length > 180 && buf) {
      chunks.push(buf.trim());
      buf = s;
    } else {
      buf = buf ? buf + " " + s : s;
    }
  }
  if (buf.trim()) chunks.push(buf.trim());
  return chunks;
};

export const Narrator = ({ story }) => {
  const { user } = useAuth();
  const isSub = isSubscriber(user);
  const [state, setState] = useState("idle"); // idle | playing | paused
  const [voice, setVoice] = useState(null);
  const queueRef = useRef([]);
  const idxRef = useRef(0);
  const cancelledRef = useRef(false);

  // Pick a nice voice on mount
  useEffect(() => {
    const pick = () => {
      const voices = window.speechSynthesis?.getVoices?.() || [];
      if (!voices.length) return;
      const preferred =
        voices.find(v => /Samantha|Serena|Karen|Google UK English Female|Microsoft Aria|Emma/i.test(v.name)) ||
        voices.find(v => v.lang?.startsWith("en") && v.name.toLowerCase().includes("female")) ||
        voices.find(v => v.lang?.startsWith("en")) ||
        voices[0];
      setVoice(preferred);
    };
    pick();
    window.speechSynthesis?.addEventListener?.("voiceschanged", pick);
    return () => window.speechSynthesis?.removeEventListener?.("voiceschanged", pick);
  }, []);

  useEffect(() => () => { try { window.speechSynthesis?.cancel(); } catch (e) {} }, []);

  const speakChunk = useCallback(() => {
    if (cancelledRef.current) return;
    if (idxRef.current >= queueRef.current.length) {
      setState("idle");
      return;
    }
    const utter = new SpeechSynthesisUtterance(queueRef.current[idxRef.current]);
    if (voice) utter.voice = voice;
    utter.rate = 0.95;
    utter.pitch = 1.0;
    utter.onend = () => {
      idxRef.current += 1;
      speakChunk();
    };
    utter.onerror = () => setState("idle");
    window.speechSynthesis.speak(utter);
  }, [voice]);

  const play = () => {
    if (!("speechSynthesis" in window)) {
      toast.error("Your browser can't narrate yet — try Chrome or Safari");
      return;
    }
    if (state === "paused") {
      window.speechSynthesis.resume();
      setState("playing");
      return;
    }
    // Log the listen for streaks
    if (story?.story_id) {
      import("@/lib/api").then(({ api }) => api.post(`/stories/${story.story_id}/listen`).catch(() => {}));
    }
    const full = (story.chapters || []).map(c => `${c.title}. ${c.content}`).join(" ");
    queueRef.current = chunkText(full);
    idxRef.current = 0;
    cancelledRef.current = false;
    setState("playing");
    speakChunk();
  };
  const pause = () => { window.speechSynthesis.pause(); setState("paused"); };
  const stop = () => {
    cancelledRef.current = true;
    try { window.speechSynthesis.cancel(); } catch (e) {}
    setState("idle");
  };

  if (!isSub) {
    return (
      <Button
        data-testid="narrator-locked"
        asChild
        variant="outline"
        size="sm"
        className="rounded-full border-amber-500/40 text-amber-300 h-8"
      >
        <Link to="/pricing"><Lock className="h-3.5 w-3.5 mr-1" /> Narrator · Subscribe</Link>
      </Button>
    );
  }

  return (
    <div className="inline-flex items-center gap-1 rounded-full border border-white/10 p-0.5">
      {state !== "playing" ? (
        <Button data-testid="narrator-play" size="sm" variant="ghost" onClick={play} className="h-7 rounded-full px-3 text-xs">
          <Play className="h-3.5 w-3.5 mr-1" /> {state === "paused" ? "Resume" : "Narrate"}
        </Button>
      ) : (
        <Button data-testid="narrator-pause" size="sm" variant="ghost" onClick={pause} className="h-7 rounded-full px-3 text-xs">
          <Pause className="h-3.5 w-3.5 mr-1" /> Pause
        </Button>
      )}
      {state !== "idle" && (
        <Button data-testid="narrator-stop" size="sm" variant="ghost" onClick={stop} className="h-7 w-7 rounded-full p-0">
          <Square className="h-3 w-3" />
        </Button>
      )}
    </div>
  );
};
